import React, { useEffect, useState } from 'react';
import { styled, useTheme } from '@mui/material/styles';
import { useAutoTranslation } from 'src/hooks/useTranslate';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Popover from '@mui/material/Popover';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import CheckIcon from '@mui/icons-material/Check';
import { DISEASE_PALETTE } from 'src/util/diseasePalette';
import EditIcon from '@mui/icons-material/Edit';

// The disease palette is no longer defined here. It lives in ONE file that the
// server allocator reads too (backend/util/disease-palette.json), because this
// list used to disagree with it: it carried only 18 of the server's 20 colours —
// omitting Cream, Mint and Peach, which the server DOES allocate, so an admin
// could not reproduce a colour the chart had already assigned — and it added
// #000000, which the server never allocates and which is indistinguishable on a
// node from a disease that has no colour at all.
//
// It also emitted UPPERCASE hexes against a mixed-case server palette. Since
// applyPlan's dirty check is case-sensitive, picking the colour a disease
// already had rewrote every member document for a byte difference. Emitting the
// shared strings verbatim closes that.
//
// Re-exported under the original name so existing imports keep working.
// `name` drives the swatch aria-labels, so the choice is never conveyed by
// colour alone (WCAG 1.4.1).
export { DISEASE_PALETTE as DISTINCT_DISEASE_COLOURS } from 'src/util/diseasePalette';

// Accepts #RGB or #RRGGBB (case-insensitive) — same contract the old picker
// validated against before emitting an onChange.
const HEX_RE = /^#([0-9a-f]{3}){1,2}$/i;
// The native <input type="color"> only accepts a 6-digit hex.
const isHex6 = (v) => /^#[0-9a-f]{6}$/i.test(v || '');

const Row = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: 12
});

// The swatch itself is the control: click/Enter/Space opens the palette.
const SwatchButton = styled(ButtonBase)(({ theme }) => ({
  position: 'relative',
  width: 40,
  height: 40,
  borderRadius: 6,
  border: `1px solid ${theme.palette.divider}`,
  overflow: 'hidden',
  '&.Mui-focusVisible': {
    outline: `2px solid ${theme.palette.primary.main}`,
    outlineOffset: 2
  }
}));

// Small corner pencil so the swatch reads as editable at a glance.
const EditBadge = styled(Box)({
  position: 'absolute',
  right: 0,
  bottom: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 16,
  height: 16,
  borderTopLeftRadius: 6,
  backgroundColor: 'rgba(255, 255, 255, 0.85)',
  color: 'rgba(0, 0, 0, 0.7)'
});

const PaletteSwatch = styled(ButtonBase)(({ theme }) => ({
  width: 30,
  height: 30,
  borderRadius: 6,
  border: `1px solid ${theme.palette.divider}`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  '&.Mui-focusVisible': {
    outline: `2px solid ${theme.palette.primary.main}`,
    outlineOffset: 2
  }
}));

const CustomToggle = styled(ButtonBase)(({ theme }) => ({
  fontSize: 13,
  color: theme.palette.text.secondary,
  padding: '2px 6px',
  borderRadius: 4,
  '&.Mui-focusVisible': {
    outline: `2px solid ${theme.palette.primary.main}`,
    outlineOffset: 2
  }
}));

const ColorPicker = ({ color, onChange }) => {
  const t = useAutoTranslation();

  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState(null);
  const [showCustom, setShowCustom] = useState(false);
  const [hexDraft, setHexDraft] = useState(color || '');

  // Keep the custom-hex draft in sync when the form value changes externally.
  useEffect(() => {
    setHexDraft(color || '');
  }, [color]);

  const open = Boolean(anchorEl);
  const currentHex = (color || '').toUpperCase();

  // Preserve the original value contract exactly: emit a synthetic event so the
  // consuming form's `handleChange` ({ target: { name, value } }) is unchanged.
  const commit = (value) => onChange({ target: { name: 'color', value } });

  const handleOpen = (e) => setAnchorEl(e.currentTarget);

  const handleClose = () => {
    setAnchorEl(null);
    setShowCustom(false);
  };

  const handlePick = (hex) => {
    commit(hex);
    handleClose();
  };

  const handleHexChange = (e) => {
    const value = e.target.value;
    setHexDraft(value);
    if (HEX_RE.test(value)) commit(value);
  };

  const isSelected = (hex) => (color || '').toLowerCase() === hex.toLowerCase();

  return (
    <>
      <Row>
        <SwatchButton
          onClick={handleOpen}
          aria-label={t('Choose colour')}
          aria-haspopup="dialog"
          sx={{ backgroundColor: color || 'transparent' }}
        >
          <EditBadge>
            <EditIcon sx={{ fontSize: 12 }} />
          </EditBadge>
        </SwatchButton>
        <Typography
          variant="body1"
          sx={{ fontFamily: 'monospace', letterSpacing: 0.5, minWidth: 84 }}
        >
          {currentHex || '—'}
        </Typography>
      </Row>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        // Eight columns, not six. At 20 colours six was four tidy rows; at 46 it
        // is eight, which overflows a short viewport. Eight columns puts 46 into
        // six rows at 296px wide, so nothing scrolls on a normal screen, and the
        // maxHeight is there for the screens where it still would.
        slotProps={{
          paper: { sx: { p: 1.5, maxWidth: 320, maxHeight: '60vh' } }
        }}
      >
        <Box
          role="group"
          aria-label={t('Colour palette')}
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(8, 30px)',
            gap: 1
          }}
        >
          {DISEASE_PALETTE.map(({ hex, name }) => {
            const selected = isSelected(hex);
            return (
              <PaletteSwatch
                key={hex}
                aria-label={`${name} (${hex})`}
                aria-pressed={selected}
                onClick={() => handlePick(hex)}
                sx={{
                  backgroundColor: hex,
                  // Ring (not just a border) so selection reads at a glance:
                  // a paper-coloured gap then a primary-coloured ring.
                  boxShadow: selected
                    ? `0 0 0 2px ${theme.palette.background.paper}, 0 0 0 4px ${theme.palette.primary.main}`
                    : 'none'
                }}
              >
                {selected && (
                  <CheckIcon
                    sx={{
                      fontSize: 18,
                      color: theme.palette.getContrastText(hex)
                    }}
                  />
                )}
              </PaletteSwatch>
            );
          })}
        </Box>

        <Box
          sx={{
            mt: 1.5,
            pt: 1,
            borderTop: `1px solid ${theme.palette.divider}`
          }}
        >
          <CustomToggle
            onClick={() => setShowCustom((s) => !s)}
            aria-expanded={showCustom}
          >
            {t('Custom…')}
          </CustomToggle>
          {showCustom && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
              <Box
                component="input"
                type="color"
                aria-label={t('Custom colour')}
                value={isHex6(hexDraft) ? hexDraft : color || '#000000'}
                onChange={handleHexChange}
                sx={{
                  width: 36,
                  height: 36,
                  p: 0,
                  border: 'none',
                  background: 'none',
                  cursor: 'pointer'
                }}
              />
              <TextField
                size="small"
                value={hexDraft}
                onChange={handleHexChange}
                placeholder={t('#RRGGBB')}
                slotProps={{
                  htmlInput: {
                    'aria-label': t('Hex colour value'),
                    maxLength: 7
                  }
                }}
                sx={{ width: 120 }}
              />
            </Box>
          )}
        </Box>
      </Popover>
    </>
  );
};

export default ColorPicker;
